import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Textarea,
  Button,
  useToast,
  Text,
  Flex,
  ButtonGroup,
  Switch,
  Tooltip,
  Icon,
  useOutsideClick,
  useColorModeValue
} from '@chakra-ui/react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import HelpModal          from './HelpModal';
import ExampleFormulas    from './ExampleFormulas';
import DPLLVisualization from './DPLLVisualization'; 




/*****************************************************************
 *  validateCNF(raw)
 *  --------------------------------------------------------------
 *  vyhodí Error pri prvej zistenenej syntaktickej chybe
 *****************************************************************/
export function validateCNF(raw) {
  const allowed = /^[\sA-Za-z0-9()¬~\-∨v|∧&^]+$/u;
  if (!allowed.test(raw))
    throw new Error('CNF obsahuje nepovolené znaky.');

  /* ---------- stavebné bloky ---------- */
  const LIT   = '(?:¬|~|-)?[A-Za-z][A-Za-z0-9]*';
  const OR    = '(?:\\s*(?:∨|v|\\|)\\s*)';
  const AND   = '(?:\\s*(?:∧|&|\\^)\\s*)';
  const CLAUSE_BODY = `${LIT}(?:${OR}${LIT})*`;
  const CLAUSE      = `\\(\\s*${CLAUSE_BODY}\\s*\\)`;

  /* ---------- globálny regex pre celú formulu ---------- */
  const fullRe = new RegExp(`^\\s*${CLAUSE}(?:${AND}${CLAUSE})*\\s*$`, 'u');
  if (!fullRe.test(raw))
    throw new Error(
      'Nekorektný formát CNF – očakáva sa ((lit ∨ lit) ∧ … ∧ (lit ∨ …)).'
    );
}


/***************************************************
 *  validateDimacs(raw) – skontroluje DIMACS vstup
 *  ------------------------------------------------
 *  • musí obsahovať presne jednu hlavičku
 *      p cnf <numVars> <numClauses>
 *  • počet klauzúl uvedený v hlavičke sa musí rovnať
 *    skutočnému počtu riadkov s klauzulou (končiacich 0)
 *  • každý literál ≠ 0 musí byť v rozsahu  ±[1, numVars]
 ***************************************************/
export function validateDimacs(raw) {
  const lines = raw
    .split('\n')
    .map(l => l.trim())
    .filter(l => l !== '' && !l.startsWith('c'));   // vynecháme komentáre

  /* —— hlavička —— */
  const header = lines.find(l => l.startsWith('p '));
  if (!header)
    throw new Error('Chýba hlavička „p cnf …“.');

  const parts = header.split(/\s+/); // ["p","cnf","<vars>","<clauses>"]
  if (parts.length !== 4 || parts[0] !== 'p' || parts[1] !== 'cnf')
    throw new Error('Hlavička musí mať tvar „p cnf <vars> <clauses>“.');

  const numVars    = parseInt(parts[2], 10);
  const numClauses = parseInt(parts[3], 10);

  if (Number.isNaN(numVars) || Number.isNaN(numClauses) ||
      numVars <= 0 || numClauses <= 0)
    throw new Error('Počty premenných aj klauzúl musia byť kladné celé čísla.');

  /* —— klauzuly —— */
  const clauseLines = lines.filter(l => l !== header);
  if (clauseLines.length === 0)
    throw new Error('V súbore nie sú žiadne klauzuly.');

  if (clauseLines.length !== numClauses)
    throw new Error(`Počet klauzúl (${clauseLines.length}) sa nezhoduje s hlavičkou (${numClauses}).`);

  clauseLines.forEach((cl, idx) => {
    const lits = cl.split(/\s+/).filter(t => t !== '');
    if (lits.at(-1) !== '0')
      throw new Error(`Klauzula č.${idx + 1} nekončí nulou.`);

    lits.slice(0, -1).forEach(numStr => {
      const lit = parseInt(numStr, 10);
      if (Number.isNaN(lit) || lit === 0)
        throw new Error(`Neplatný literál „${numStr}“ v klauzule č.${idx + 1}.`);
      if (Math.abs(lit) > numVars)
        throw new Error(`Literál „${lit}“ presahuje počet premenných (${numVars}).`);
    });
  });
}

  
/*****************************************************************
 * parseCNF(input)  – vráti  [ [lit1, lit2 …], … ]
 *****************************************************************/
export function parseCNF(input) {
  /* <-  *** vyhodí, ak vstup nie je korektný *** */
  validateCNF(input);

  /* ---- unifikujeme symboly a odstránime vonkajšie zátvorky ---- */
  const normal = input
    .replace(/\s+/g, '')          // odstráň medzery
    .replace(/\^|∧/g, '&')        // všetky AND → &
    .replace(/\|/g, '∨');         // všetky alternatívne OR → ∨

  /* ---- rozdelíme na klauzuly ---------------------------------- */
  const clauseStrings = normal
    .slice(1, -1)                 // odstránime prvú a poslednú ‘(’ ‘)’
    .split(')&(');                // )&( je po znormalizovaní jediný top‑level AND

  const clauses = clauseStrings.map(cl => cl.split(/[∨v]/));
  return clauses;
}

  
  function parseDimacs(raw) {
    validateDimacs(raw);                   // ⬅️ validácia
    const lines = raw.split('\n');
    const clauses = [];
  
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed[0] === 'c' || trimmed[0] === 'p') continue;
  
      const lits = trimmed.split(/\s+/).filter(t => t !== '0' && t !== '');
      clauses.push(
        lits.map(numStr =>
          numStr.startsWith('-') ? '¬x' + numStr.slice(1) : 'x' + numStr
        )
      );
    }
    return clauses;
  }
  


function gatherVariables(clauses) {
  // Mapa pre premennú (varName) a jej počet výskytov (freq)
  const varCount = {};

  for (const clause of clauses) {
    for (const lit of clause) {
      // Zistíme skutočný názov premennej (bez negácie)
      const neg = lit.startsWith('¬') || lit.startsWith('~') || lit.startsWith('-');
      const varName = neg ? lit.slice(1) : lit;

      // Zvýšime čítač o 1
      if (!varCount[varName]) {
        varCount[varName] = 0;
      }
      varCount[varName]++;
    }
  }

  // Vyrobíme pole z kľúčov varCount a setriedime od najčastejšie sa vyskytujúcej
  const sortedVars = Object.keys(varCount).sort((a, b) => {
    return varCount[b] - varCount[a];
  });

  return sortedVars;
}

function evaluateClause(clause, assignment) {
  // Klauzula je splnená, ak aspoň jeden literál je true
  for (const lit of clause) {
    const neg = lit.startsWith('¬') || lit.startsWith('~') || lit.startsWith('-');
    const varName = neg ? lit.slice(1) : lit;
    const literalValue = neg ? !assignment[varName] : assignment[varName];
    if (literalValue) {
      return true; // klauzula je true
    }
  }
  return false; // žiadny literal nevyšiel true
}

function evaluateFormula(clauses, assignment) {
  // Formula je splnená, ak všetky klauzuly sú splnené
  for (const c of clauses) {
    if (!evaluateClause(c, assignment)) {
      return false;
    }
  }
  return true;
}

/**
 * Bruteforce test: pre všetky priradenia (2^n) skontroluje,
 * či existuje také, ktoré splní formulu => isSatisfiable
 */
function isSatisfiable(clauses) {
  const vars = gatherVariables(clauses);
  const n = vars.length;
  const total = 1 << n;
  for (let mask = 0; mask < total; mask++) {
    const assignment = {};
    for (let i = 0; i < n; i++) {
      assignment[vars[i]] = (mask & (1 << i)) !== 0;
    }
    if (evaluateFormula(clauses, assignment)) {
      return true;
    }
  }
  return false;
}



/* ------------------------------------------------------------------
   (3) DPLL solver s budovaním rozhodovacieho stromu
   ------------------------------------------------------------------ */
// Zistí stav klauzuly vzhľadom na aktuálne assignment
function evaluateClauseState(clause, assignment) {
  let unassignedCount = 0;
  let unassignedLit = null;
  let clauseTrue = false;

  for (const lit of clause) {
    const neg = lit.startsWith('¬') || lit.startsWith('~') || lit.startsWith('-');
    const varName = neg ? lit.slice(1) : lit;
    if (varName in assignment) {
      const val = neg ? !assignment[varName] : assignment[varName];
      if (val) {
        clauseTrue = true;
        break;
      }
    } else {
      unassignedCount++;
      unassignedLit = lit;
    }
  }
  if (clauseTrue) return 'clauseTrue';
  if (unassignedCount === 0) return 'conflict';
  if (unassignedCount === 1) {
    const neg = unassignedLit.startsWith('¬') || unassignedLit.startsWith('~') || unassignedLit.startsWith('-');
    const varName = neg ? unassignedLit.slice(1) : unassignedLit;
    const value = !neg;
    return { unitLiteral: { variable: varName, value } };
  }
  return 'undecided';
}

function unitPropagation(clauses, assignment, steps, currentNode) {
  // Jednotková propagácia kým nachádzame unit klauzuly
  while (true) {
    let foundUnit = false;

    for (const clause of clauses) {
      const st = evaluateClauseState(clause, assignment);

      if (st === 'clauseTrue') {
        continue; // klauzula už splnená
      }
      if (st === 'conflict') {
        return { conflict: true };
      }
      if (st.unitLiteral) {
        const { variable, value } = st.unitLiteral;
        assignment[variable] = value;
        steps.push({
          decision: `Unit propagation: ${variable}=${value}`,
          unitProps: [`${variable}=${value}`],
          backtrack: false
        });
        // Do node-u si môžeme ukladať metadáta, ak chceme
        if (!currentNode.attributes) currentNode.attributes = {};
        if (!currentNode.attributes.unitProps) {
          //currentNode.attributes.unitProps = [];
        }
        //currentNode.attributes.unitProps.push(`${variable}=${value}`);
        foundUnit = true;
        break; // znovu začneme from scratch
      }
    }

    if (!foundUnit) {
      return { conflict: false };
    }
  }
}



function dpllRecursive(
  clauses,
  variables,
  index,
  assignment,
  steps,
  currentNode,
  statsTracker
) {
  // Inkrementujeme čítač rekurzívnych volaní
  statsTracker.recursiveCalls++;

  // 1) Jednotková propagácia
  const upResult = unitPropagation(clauses, assignment, steps, currentNode);
  if (upResult.conflict) {
    steps.push({
      decision: `Conflict after unit propagation at level ${index}`,
      unitProps: [],
      backrack: true
    });
    currentNode.attributes = { conflict: true };
    return false;
  }

  // 2) Ak už sú všetky premenné priradené, testneme evaluateFormula
  if (index >= variables.length) {
    if (evaluateFormula(clauses, assignment)) {
      steps.push({
        decision: 'All assigned => formula is SAT',
        unitProps: [],
        backtrack: false
      });
      currentNode.attributes = { satLeaf: true };
      return true;
    }
    return false;
  }

  const variable = variables[index];

  // -----------------------------------------
  // Vetva: variable = true
  // -----------------------------------------
  const creationStepTrue = steps.length;
  const trueNode = {
    name: `${variable}=true`,
    children: [],
    creationStep: creationStepTrue,
    id: `step-${creationStepTrue}`
  };
  currentNode.children.push(trueNode);

  steps.push({
    decision: `Assign ${variable}=true`,
    //unitProps: [],
    backtrack: false
  });
  assignment[variable] = true;

  if (
    dpllRecursive(
      clauses,
      variables,
      index + 1,
      assignment,
      steps,
      trueNode,
      statsTracker
    )
  ) {
    return true;
  }

  // Backtrack
  delete assignment[variable];
  steps.push({
    decision: `Backtrack from ${variable}=true`,
    unitProps: [],
    backtrack: true
  });
  trueNode.attributes = { conflict: true };

  // -----------------------------------------
  // Vetva: variable = false
  // -----------------------------------------
  const creationStepFalse = steps.length;
  const falseNode = {
    name: `${variable}=false`,
    children: [],
    creationStep: creationStepFalse,
    id: `step-${creationStepFalse}`
  };
  currentNode.children.push(falseNode);

  steps.push({
    decision: `Assign ${variable}=false`,
    unitProps: [],
    backtrack: false
  });
  assignment[variable] = false;

  if (
    dpllRecursive(
      clauses,
      variables,
      index + 1,
      assignment,
      steps,
      falseNode,
      statsTracker
    )
  ) {
    return true;
  }

  // znova backtrack
  delete assignment[variable];
  steps.push({
    decision: `Backtrack from ${variable}=false`,
    unitProps: [],
    backtrack: true
  });
  falseNode.attributes = { conflict: true };

  return false;
}

function runDPLLAndBuildTree(clauses) {
  const variables = gatherVariables(clauses);

  // Tu budeme sledovať rekurzívne volania
  const statsTracker = {
    recursiveCalls: 0
  };

  const steps = [];
  const root = {
    name: 'DPLL Root',
    children: [],
    creationStep: 0
  };

  const assignment = {};
  const sat = dpllRecursive(
    clauses,
    variables,
    0,
    assignment,
    steps,
    root,
    statsTracker
  );

  return {
    steps,
    treeData: root,
    isSat: sat,
    statsTracker,
    assignment
  };
}

function getAssignmentUpToStep(steps, stepIdx) {
  const assignment = {};
  for (let i = 0; i <= stepIdx; i++) {
    const step = steps[i];
    const m = step?.decision?.match(/([a-zA-Z0-9_]+)=(true|false)/);
    if (m) {
      const [, v, val] = m;
      assignment[v] = val === 'true';
    }
    step?.unitProps?.forEach(str => {
      const [v, val] = str.split('=');
      assignment[v] = val === 'true';
    });
  }
  return assignment;
}

/* ---------------------------------------------------------
   (4) InputCNFForm
   --------------------------------------------------------- */
const InputCNFForm = ({ onSolve }) => {
  const [cnfText, setCnfText] = useState('');
  const [parsedFormula, setParsedFormula] = useState(null);
  const [isSat, setIsSat] = useState(null);
  const [useDimacs, setUseDimacs] = useState(false); // prepínač formátu

  // Tu budeme ukladať štatistiky behu
  const [stats, setStats] = useState(null);

  const textAreaRef = useRef(null);
  const toast = useToast();

  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const shortcutsRef = useRef(null);

  /* ---------------------- farby (light/dark) ------------------ */
  const bgContainer  = useColorModeValue('white',      'gray.800');
  const bgPanel      = useColorModeValue('gray.100',   'gray.700');
  const textColor    = useColorModeValue('black',      'white');

  const [dpllSteps, setDpllSteps]     = useState([]);  // všetky kroky
  const [treeData, setTreeData]       = useState(null);
  const [currentStepIdx, setStepIdx]  = useState(0);   // “globálny” index kroku

  /* helper – aktuálne priradenie podľa kroku (už ho v súbore máš) */
  const currentAssignment = getAssignmentUpToStep(dpllSteps, currentStepIdx);




/* kliknutie mimo tooltip‑u ho zavrie – praktické na mobile */
useOutsideClick({
  ref: shortcutsRef,
  handler: () => setShortcutsOpen(false),
}); 

  /**
   * autoCompleteShortcuts
   * - v reálnom čase nahrádza niektoré skratky.
   *   Napr. !! => ¬, && => ∧, || => ∨, ! => ¬
   */
  const autoCompleteShortcuts = (raw) => {
    let replaced = raw
      .replace(/\!\!/g, '¬')
      .replace(/\!(?![a-zA-Z])/g, '¬')
      .replace(/\&\&/g, '∧')
      .replace(/\|\|/g, '∨');
    return replaced;
  };

  const handleChange = (e) => {
    let newVal = e.target.value;
    newVal = autoCompleteShortcuts(newVal);
    setCnfText(newVal);
  };

  const handleInsertSymbol = (symbol) => {
    if (!textAreaRef.current) return;
    const el = textAreaRef.current;
    const { selectionStart, selectionEnd } = el;
    const before = cnfText.slice(0, selectionStart);
    const after = cnfText.slice(selectionEnd);
    const newText = before + symbol + after;
    setCnfText(newText);

    setTimeout(() => {
      el.selectionStart = el.selectionEnd = selectionStart + symbol.length;
      el.focus();
    }, 0);
  };

  // Prepínač CNF vs. DIMACS
  const handleToggleDimacs = (e) => {
    setUseDimacs(e.target.checked);
    setCnfText('');
    setParsedFormula(null);
    setIsSat(null);
    setStats(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    try {
      let parsed;
      if (useDimacs) {
        parsed = parseDimacs(cnfText);
      } else {
        parsed = parseCNF(cnfText);
      }
      setParsedFormula(parsed);

      // Zmeráme čas pred spustením solvera
      const t0 = performance.now();

      // Spustíme solver (DPLL + building tree)
      const { steps, treeData, isSat: finalSat, statsTracker,assignment } = runDPLLAndBuildTree(parsed);

      // Zmeráme čas po dokončení
      const t1 = performance.now();
      const elapsedMs = t1 - t0;

      // Zistíme splniteľnosť aj bruteforce-om (nepovinné, ale v príklade to bolo)
      const satisfiable = isSatisfiable(parsed);

      setIsSat(finalSat);
      setDpllSteps(steps);   // ←
      setTreeData(treeData); // ←
      setStepIdx(0);    
            
      setDpllSteps(steps);
      
      // Uložíme štatistiky
      const newStats = {
        elapsedTimeMs: elapsedMs,
        recursiveCalls: statsTracker.recursiveCalls,
        numClauses: parsed.length,
        numVariables: gatherVariables(parsed).length,
        assignment: {...assignment} // ← pridaj aktuálne priradenie
      };
      setStats(newStats);

      // Zavoláme onSolve, ak potrebujeme zobraziť kroky a strom v inej časti aplikácie
      if (onSolve) {
        onSolve(steps, finalSat, treeData);
      }

      toast({
        title: 'Výpočet dokončený',
        description: finalSat
          ? 'Formula je splniteľná (SAT)'
          : 'Formula je nesplniteľná (UNSAT)',
        status: finalSat ? 'success' : 'warning',
        duration: 4000,
        isClosable: true
      });
    } catch (err) {
      toast({
        title: 'Neplatný vstup',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  

  // Callback z ExampleFormulas
  const handleExampleSelect = useCallback((formula) => {
    setCnfText(formula);
  }, []);

  return (

    
    <Box bg={bgContainer} p={4} boxShadow="md" color={textColor}>
      {/* --------------------------------- HLAVIČKA + HELP ------- */}
      <Flex justify="space-between" align="center" mb={2}>
        <FormLabel htmlFor="cnf-input" mb={0}>
          {useDimacs ? 'Zadajte DIMACS formulu:' : 'Zadajte CNF formulu:'}
        </FormLabel>
        <HelpModal useDimacs={useDimacs}/>
      </Flex>

      {/* --------------------------- PREPÍNAČ  CNF / DIMACS ------ */}
      <Box mb={2}>
        <Flex align="center" gap={2}>
          <Text fontWeight={useDimacs ? 'normal':'bold'}
                color={useDimacs ? 'gray.500':'blue.500'}>CNF</Text>

          <Switch
            id="format-switch"
            isChecked={useDimacs}
            onChange={handleToggleDimacs}
            aria-label="Prepni medzi CNF a DIMACS"
            sx={{
              'span.chakra-switch__track': { bg:'blue.500' },
              '&[data-checked] span.chakra-switch__track': { bg:'blue.500' },
              'span.chakra-switch__thumb': { bg:'white' },
            }}
          />

          <Text fontWeight={useDimacs ? 'bold':'normal'}
                color={useDimacs ? 'blue.500':'gray.500'}>DIMACS</Text>
        </Flex>
      </Box>

      {/* ----------------------- PRÍKLADY ------------------------ */}
      <ExampleFormulas isDimacs={useDimacs} onSelect={handleExampleSelect}/>

      {/* ----------------------- FORM --------------------------- */}
      <form onSubmit={handleSubmit}>
        <FormControl mt={4}>
          {/* ---- Tooltip / skratky (klik aj hover) ---- */}
          {!useDimacs && (
            <Tooltip
              label="Automatické doplňovanie: !! → ¬, && → ∧, || → ∨, ! → ¬"
              hasArrow
              placement="top-start"
              bg="blue.300"
              isOpen={shortcutsOpen}
            >
              <Flex ref={shortcutsRef} align="center" mb={1} cursor="pointer"
                    onClick={()=>setShortcutsOpen(o=>!o)}>
                <Icon as={InfoOutlineIcon} mr={1}/>
                <Text fontSize="sm" color="gray.600">Skratky pre logické symboly</Text>
              </Flex>
            </Tooltip>
          )}

          {/* ---- rýchlovkladanie symbolov ---- */}
          {!useDimacs && (
            <ButtonGroup size="sm" spacing={1} mb={2}>
              {['¬','∨','∧'].map(sym=>(
                <Button key={sym} variant="outline" onClick={()=>handleInsertSymbol(sym)}>{sym}</Button>
              ))}
            </ButtonGroup>
          )}

          {/* ---- Textarea ---- */}
          <Textarea
            ref={textAreaRef}
            id="cnf-input"
            placeholder={useDimacs
              ? 'p cnf 3 2\n1 -2 0\n2 3 0'
              : '(p ∨ ¬q ∨ r) ∧ (¬p ∨ s) …'}
            value={cnfText}
            onChange={handleChange}
            rows={8}
            fontSize="x-large"
            bg={useColorModeValue('white','gray.700')}
            color={textColor}
            _placeholder={{color: useColorModeValue('gray.400','gray.500')}}
          />
        </FormControl>

        {/* ---- Submit / Clear ---- */}
        <Flex mt={4} gap={2}>
          <Button type="submit" colorScheme="blue" isDisabled={cnfText.trim()===''}>Vyhodnotiť</Button>
          <Button variant="outline" onClick={()=>{
            setCnfText(''); setParsedFormula(null); setIsSat(null); setStats(null);
            onSolve?.([],null,null);
          }}>Vymazať</Button>
        </Flex>
      </form>

      {/* ----------------------- VÝPIS KLAUZÚL ------------------- */}
      {parsedFormula && stats && (
  <Box mt={4} p={2} bg={bgPanel} borderRadius="md">
    <Text fontWeight="semibold" mb={2}>
      Stav klauzúl ({parsedFormula.length}):
    </Text>
    <Box mt={1} ml={4}>
      {parsedFormula.map((clause, idx) => {
        const clauseState = evaluateClauseState(clause, stats.assignment || {});
        let color = 'gray.400';
        let label = 'undecided';

        if (clauseState === 'clauseTrue') {
          color = 'green.400';
          label = '';
        } 

        return (
          <Text key={idx} color={color}>
            {idx + 1}. ({clause.join(' ∨ ')})
          </Text>
        );
      })}
    </Box>
    <Text mt={2} fontWeight="semibold" color="blue.500">
      Splniteľnosť: {isSat ? 'SAT' : 'UNSAT'}
    </Text>
  </Box>
)}

      {/* ----------------------- ŠTATISTIKY ---------------------- */}
      {stats && (
        <Box mt={4} p={2} bg={bgPanel} borderRadius="md">
          <Text fontWeight="bold">Štatistiky behu:</Text>
          <Text>Čas behu: {stats.elapsedTimeMs.toFixed(8)} ms</Text>
          <Text>Rekurzívnych volaní: {stats.recursiveCalls}</Text>
          <Text>Počet klauzúl: {stats.numClauses}</Text>
          <Text>Počet premenných: {stats.numVariables}</Text>
        </Box>
      )}
      </Box>
  );
};

export default InputCNFForm;
